// src/pages/community/radio_stations/MusicRadioDetail.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  deleteDoc,
  where,
} from "firebase/firestore";
import { db } from "../../../firebase";
import PageWrapper from "../../../components/PageWrapper";
import ReactMarkdown from "react-markdown";
import { Helmet } from "react-helmet";
import "./css/MusicRadioDetail.css";
// <- new imports
import { PresenceBadge, LikeBar, Discussion } from "../components";
// Add these imports near the top with the other firebase imports
import { getFunctions, httpsCallable } from "firebase/functions";
import { enqueueReactionGive, enqueueReactionUndo } from "../../../services/xpService";

/* ---------- helpers ---------- */
const ensureHttps = (u = "") => {
  const s = String(u || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  return `https://${s.replace(/^\/+/, "")}`;
};

const toSpotifyEmbed = (url = "") => {
  const raw = ensureHttps(url);
  try {
    if (/^spotify:/i.test(raw)) {
      const [, type, id] = raw.split(":");
      if (type && id) return `https://open.spotify.com/embed/${type}/${id}`;
      return null;
    }
    const u = new URL(raw);
    if (!/open\.spotify\.com$/i.test(u.hostname)) return null;
    const parts = u.pathname.replace(/^\/+/, "").split("/");
    const p = parts[0] === "embed" ? parts.slice(1) : parts;
    const kind = p[0];
    const id = p[1];
    if (!kind || !id) return null;
    return `https://open.spotify.com/embed/${kind}/${id}`;
  } catch (err) {
    console.error("toSpotifyEmbed error:", err, url);
    return null;
  }
};

const toYouTubeEmbed = (url = "") => {
  const raw = ensureHttps(url);
  try {
    const u = new URL(raw);
    if (!/youtube\.com$|youtu\.be$/i.test(u.hostname)) return null;
    const list = u.searchParams.get("list");
    const v = u.searchParams.get("v");
    if (list && !v) return `https://www.youtube.com/embed/videoseries?list=${list}`;
    let id = "";
    if (u.hostname.includes("youtu.be")) id = u.pathname.slice(1);
    else if (v) id = v;
    if (!id) return null;
    return `https://www.youtube.com/embed/${id}`;
  } catch (err) {
    console.error("toYouTubeEmbed error:", err, url);
    return null;
  }
};

const toAppleMusicEmbed = (url = "") => {
  const raw = ensureHttps(url);
  try {
    const u = new URL(raw);
    if (!/music\.apple\.com$/i.test(u.hostname)) return null;
    u.hostname = "embed.music.apple.com";
    return u.toString();
  } catch (err) {
    console.error("toAppleMusicEmbed error:", err, url);
    return null;
  }
};

const mapDoc = (id, x = {}) => {
  const cover =
    x.coverImage ||
    (Array.isArray(x.gallery) && x.gallery.length > 0 ? x.gallery[0] : "") ||
    x.image ||
    x.imageUrl ||
    "";

  const createdAtMs =
    x.createdAt?.toMillis?.() ?? (typeof x.createdAt === "number" ? x.createdAt : 0);

  return {
    id,
    slug: x.slug || null,
    name: x.name || "Untitled Station",
    tagline: x.tagline || "",
    game: x.game || "",
    cover,
    frequency: x.frequency || "",
    city: x.city || "",
    djs: Array.isArray(x.djs) ? x.djs : [],
    genres: Array.isArray(x.genres) ? x.genres : [],
    spotifyUrl: x.spotifyUrl || "",
    youtubeUrl: x.youtubeUrl || "",
    appleMusicUrl: x.appleMusicUrl || "",
    description: x.description || "",
    trivia: x.trivia || "",
    gallery: Array.isArray(x.gallery) ? x.gallery : [],
    tags: Array.isArray(x.tags) ? x.tags : [],
    createdAtMs,
    // if your station docs have createdBy, it will surface here automatically
    createdBy: x.createdBy ?? null,
  };
};

/* ---------- reactions config (updated per request) ---------- */
// Requested reaction set: Love, Hot, Applause, Cool, Wow, Music
const REACTION_KEYS = ["love", "fire", "applause", "cool", "wow", "music"];
const emptyCounts = () =>
  REACTION_KEYS.reduce((acc, k) => {
    acc[k] = 0;
    return acc;
  }, {});

/* ------------------------------------------------------------------------------------------------- */

// call server-side callable to update achievements (safer than client writes)
async function updateAchievementCounts(postId, deltaMap = {}, postOwnerUid = null) {
  if (!postId && !postOwnerUid) return;
  try {
    const functions = getFunctions();
    const fn = httpsCallable(functions, "updateAchievements");
    const payload = { postId: postId || "", deltaMap, source: "reaction", postOwnerUid };
    const res = await fn(payload);
    console.debug("updateAchievements callable result", { postId, postOwnerUid, res: res?.data });
    return res?.data ?? null;
  } catch (err) {
    console.error("updateAchievementCounts (callable) failed", err, { postId, deltaMap, postOwnerUid });
    return null;
  }
}

export default function MusicRadioDetail({ user: propUser }) {
  const outlet = useOutletContext?.() || {};
  const user = propUser ?? outlet.user;

  const { slugOrId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [station, setStation] = useState(null);
  const [notFound, setNotFound] = useState(false);

  // reactions
  const [reactionCounts, setReactionCounts] = useState(emptyCounts());
  const [myReactions, setMyReactions] = useState({}); // { love: true, ... }
  const [reactBusy, setReactBusy] = useState(false);

  // basePath for LikeBar / Discussion
  const basePath = useMemo(
    () => (station?.id ? ["radio_stations", station.id] : null),
    [station?.id]
  );

  /* ---------- global error hooks (helpful while debugging) ---------- */
  useEffect(() => {
    const onError = (event) => {
      console.error("Global error caught:", event);
    };
    const onRejection = (e) => {
      console.error("Unhandled promise rejection:", e);
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  /* ---------- load station by ID OR slug ---------- */
  useEffect(() => {
    let isActive = true;

    const loadStation = async () => {
      setLoading(true);
      setNotFound(false);
      setStation(null);
      console.debug("Loading station:", slugOrId);

      try {
        // try doc id
        const byId = await getDoc(doc(db, "radio_stations", slugOrId));
        if (byId.exists()) {
          if (isActive) setStation(mapDoc(byId.id, byId.data()));
          setLoading(false);
          console.debug("Loaded station by id:", byId.id);
          return;
        }

        // try by slug
        const q = query(
          collection(db, "radio_stations"),
          where("slug", "==", slugOrId),
          limit(1)
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
          const d = snap.docs[0];
          if (isActive) setStation(mapDoc(d.id, d.data()));
          setLoading(false);
          console.debug("Loaded station by slug:", d.id);
          return;
        }

        if (isActive) setNotFound(true);
        console.warn("Station not found for:", slugOrId);
      } catch (err) {
        console.error("Error loading radio station:", err, slugOrId);
        if (isActive) setNotFound(true);
      } finally {
        if (isActive) setLoading(false);
      }
    };

    loadStation();
    return () => {
      isActive = false;
    };
  }, [slugOrId]);

  /* ---------- listeners: reactions (real-time) ---------- */
  useEffect(() => {
    if (!station?.id) return;

    console.debug("Attaching listeners for station reactions:", station.id);

    // REACTIONS: we store each user's reaction flags under
    // /radio_stations/{id}/reactions/{docId}  => { type: 'love', createdAt: ... }
    const reactionsRef = collection(db, "radio_stations", station.id, "reactions");
    const unsubReactions = onSnapshot(
      reactionsRef,
      (snap) => {
        try {
          const counts = emptyCounts();
          let mineType = null;

          snap.forEach((d) => {
            const data = d.data() || {};
            const type = data.type || null;
            // only count valid reaction types
            if (type && REACTION_KEYS.includes(type)) {
              counts[type] = (counts[type] || 0) + 1;
            }
            // capture current user's reaction (if present) -- follow the "global_{uid}" pattern if used
            if (user?.uid && d.id === `global_${user.uid}`) {
              mineType = type || null;
            }
          });

          setReactionCounts(counts);
          // keep myReactions as an object shape used elsewhere in the component
          setMyReactions(mineType ? { [mineType]: true } : {});
        } catch (err) {
          console.error("Error processing reactions snapshot:", err);
        }
      },
      (err) => {
        console.error("Reactions onSnapshot error:", err);
      }
    );

    return () => {
      try {
        unsubReactions?.();
      } catch (e) {
        console.warn("Error unsubscribing reactions:", e);
      }
    };
  }, [station?.id, user?.uid]);

  const embedSrc = useMemo(() => {
    if (!station) return null;
    return (
      toSpotifyEmbed(station.spotifyUrl) ||
      toYouTubeEmbed(station.youtubeUrl) ||
      toAppleMusicEmbed(station.appleMusicUrl) ||
      null
    );
  }, [station]);

  /* ---------- actions ---------- */
const toggleReaction = useCallback(
  async (key) => {
    if (!user?.uid) {
      console.warn("toggleReaction: user not signed in");
      return;
    }
    if (!station?.id) {
      console.warn("toggleReaction: station not loaded yet");
      return;
    }
    if (!REACTION_KEYS.includes(key)) {
      console.warn("toggleReaction: invalid reaction key:", key);
      return;
    }
    if (reactBusy) {
      console.debug("toggleReaction: busy, skipping");
      return;
    }

    setReactBusy(true);
    try {
      const rid = `global_${user.uid}`;
      const ref = doc(db, "radio_stations", station.id, "reactions", rid);

      // determine whether this key is currently selected by reading myReactions
      const currentlySelected = Boolean(myReactions?.[key]);

      // try to resolve post owner uid (prefer station.createdBy if present)
      const postId = station.id;
      let postOwnerUid = station.createdBy ?? null;

      // fallback: try to read station doc if createdBy not present (best-effort)
      if (!postOwnerUid) {
        try {
          const parentSnap = await getDoc(doc(db, "radio_stations", station.id));
          if (parentSnap.exists()) {
            const pdata = parentSnap.data() || {};
            postOwnerUid =
              pdata.ownerUid || pdata.authorUid || pdata.uid || pdata.userId || pdata.createdBy || null;
          }
        } catch (e) {
          console.warn("Could not resolve post owner uid for achievements:", e);
        }
      }

      const isBug = key === "bug";

      if (currentlySelected) {
        console.debug("Attempting to delete reaction doc:", ref.path);
        await deleteDoc(ref);
        console.debug("Deleted reaction doc:", ref.path);

        // Adjust achievements: decrement the reaction type for the post owner
        try {
          await updateAchievementCounts(postId, { [key]: -1 }, postOwnerUid);
        } catch (e) {
          console.error("updateAchievementCounts failed (unselect)", e);
        }

        // enqueue XP undo for non-bug reactions
        if (!isBug && user?.uid) {
          await enqueueReactionUndo({
            giverUid: user.uid,
            postId,
            postOwnerUid,
            postCollection: "radio_stations",
            reactionId: key,
          }).catch((e) =>
            console.error("enqueueReactionUndo failed", e, {
              postId,
              reactionId: key,
              postOwnerUid,
            })
          );
        } else {
          console.debug("skip XP undo (bug or missing uid)", { key });
        }
      } else {
        const payload = { type: key, createdAt: serverTimestamp() };
        console.debug("Attempting to write reaction doc:", ref.path, payload);
        // write WITHOUT merge so request.resource has ONLY type + createdAt
        // note: setDoc without merge to ensure security rules are simple
        await setDoc(ref, payload);
        console.debug("Wrote reaction doc:", ref.path, { type: key });

        // Adjust achievements: increment the reaction type for the post owner
        try {
          await updateAchievementCounts(postId, { [key]: 1 }, postOwnerUid);
        } catch (e) {
          console.error("updateAchievementCounts failed (select)", e);
        }

        // enqueue XP give for non-bug reactions
        if (!isBug && user?.uid) {
          await enqueueReactionGive({
            giverUid: user.uid,
            postId,
            postOwnerUid,
            postCollection: "radio_stations",
            reactionId: key,
          }).catch((e) =>
            console.error("enqueueReactionGive failed", e, {
              postId,
              reactionId: key,
              postOwnerUid,
            })
          );
        } else {
          console.debug("skip XP give (bug or missing uid)", { key });
        }
      }
    } catch (err) {
      console.error("toggleReaction error:", err);
      if (err?.code === "permission-denied") {
        console.error("Permission denied when toggling reaction. Possible causes:");
        console.error("- writing to wrong doc id (must be global_<uid>)");
        console.error("- extra/disallowed fields in payload (rules only allow 'type' and 'createdAt')");
        console.error("- Firestore rules deployed are older version");
      }
    } finally {
      setReactBusy(false);
    }
  },
  [user?.uid, station?.id, myReactions, reactBusy]
);


  const useCanonical = () => {
    if (station?.slug && station.slug !== slugOrId) {
      navigate(`/community/radio_stations/${station.slug}`, { replace: true });
    }
  };

  /* --------------------------------- render --------------------------------- */
  return (
    <PageWrapper>
      <div className="mr-detail">
        <Helmet>
          <title>{station?.name ? `${station.name} | Music & Radio` : "Music & Radio"}</title>
          <meta name="description" content={station?.tagline || "GTA music & radio station"} />
        </Helmet>

        <div className="mr-detail-bar">
          <Link to="/community/radio_stations" className="mr-back">
            ← All Stations
          </Link>
          {!!station?.slug && station.slug !== slugOrId && (
            <button className="mr-canonical" onClick={useCanonical} title="Go to canonical URL">
              Use canonical URL
            </button>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div className="mrd-skel">
            <div className="sk-cover" />
            <div className="sk-title" />
            <div className="sk-line" />
            <div className="sk-line short" />
          </div>
        )}

        {/* Not found */}
        {!loading && notFound && (
          <div className="mr-notfound">
            <h2>Page not found</h2>
            <p>We can’t seem to find this station.</p>
            <Link to="/community/radio_stations" className="mr-btn">
              Home
            </Link>
          </div>
        )}

        {/* Content */}
        {!loading && station && (
          <>
            {/* HERO */}
            <div className="mr-hero">
              <div className="mr-hero-cover">
                {station.cover ? (
                  <img src={ensureHttps(station.cover)} alt={`${station.name} cover`} loading="lazy" />
                ) : (
                  <div className="mr-hero-cover--ph">🎵</div>
                )}
              </div>

              <div className="mr-hero-body">
                <h1 className="mr-title">{station.name}</h1>
                {station.tagline && <p className="mr-tagline">{station.tagline}</p>}

                <div className="mr-chips">
                  {station.game && <span className="chip">🎮 {station.game}</span>}
                  {station.frequency && <span className="chip">📻 {station.frequency}</span>}
                  {station.city && <span className="chip">📍 {station.city}</span>}
                  {station.djs?.length > 0 && (
                    <span className="chip">🎙 {station.djs.join(", ")}</span>
                  )}
                  {Array.isArray(station.genres) &&
                    station.genres.map((g) => (
                      <span className="chip" key={g}>
                        #{g}
                      </span>
                    ))}
                </div>

                <div className="mr-links">
                  {station.spotifyUrl && (
                    <a
                      href={ensureHttps(station.spotifyUrl)}
                      target="_blank"
                      rel="noreferrer"
                      className="mr-btn"
                    >
                      Open on Spotify
                    </a>
                  )}
                  {station.youtubeUrl && (
                    <a
                      href={ensureHttps(station.youtubeUrl)}
                      target="_blank"
                      rel="noreferrer"
                      className="mr-btn"
                    >
                      Open on YouTube
                    </a>
                  )}
                  {station.appleMusicUrl && (
                    <a
                      href={ensureHttps(station.appleMusicUrl)}
                      target="_blank"
                      rel="noreferrer"
                      className="mr-btn"
                    >
                      Open on Apple Music
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* PLAYER */}
            <div className="mr-player-wrap">
              {embedSrc ? (
                <iframe
                  title="station-player"
                  src={embedSrc}
                  width="100%"
                  height={embedSrc.includes("youtube.com") ? 360 : 190}
                  frameBorder="0"
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                  allowFullScreen
                />
              ) : (
                <div className="mr-player-empty">No playable link added.</div>
              )}
            </div>

            {/* DESCRIPTION */}
            {station.description?.trim() && (
              <section className="mr-section">
                <h3>Description</h3>
                <div className="mr-md">
                  <ReactMarkdown>{station.description}</ReactMarkdown>
                </div>
              </section>
            )}

            {/* TRIVIA */}
            {station.trivia?.trim() && (
              <section className="mr-section">
                <h3>Trivia & Notes</h3>
                <div className="mr-md">
                  <ReactMarkdown>{station.trivia}</ReactMarkdown>
                </div>
              </section>
            )}

            {/* GALLERY */}
            {station.gallery?.length > 0 && (
              <section className="mr-section">
                <h3>Gallery</h3>
                <div className="mr-gallery">
                  {station.gallery.map((u, i) => (
                    <a
                      key={`${u}-${i}`}
                      href={ensureHttps(u)}
                      target="_blank"
                      rel="noreferrer"
                      className="mr-g-item"
                    >
                      <img src={ensureHttps(u)} alt="" loading="lazy" />
                    </a>
                  ))}
                </div>
              </section>
            )}

            {/* TAGS */}
            {station.tags?.length > 0 && (
              <section className="mr-section">
                <h3>Tags</h3>
                <div className="mr-tags">
                  {station.tags.map((t) => (
                    <span className="chip" key={t}>
                      #{t}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {/* --- REACTIONS (kept) with PresenceBadge + LikeBar */}
            <section className="mr-section">
              <h3>Reactions</h3>

              <div className="mr-react-share">
                <div className="mr-presence-like">
                  {/* user viewing now */}
                  <PresenceBadge presenceKey="radio_stations" entityId={station.id} />
                  {/* like button (works like MissionDetail) */}
                  {basePath && <LikeBar basePath={basePath} />}
                </div>

                <div className="mr-reactions">
                  {REACTION_KEYS.map((k) => (
                    <button
                      key={k}
                      className={`mr-react ${myReactions?.[k] ? "active" : ""}`}
                      onClick={() => toggleReaction(k)}
                      disabled={!user || reactBusy}
                      title={user ? `React: ${k}` : "Sign in to react"}
                    >
                      <span className="mr-react-emoji">
                        {k === "love"
                          ? "❤️"
                          : k === "fire"
                          ? "🔥"
                          : k === "applause"
                          ? "👏"
                          : k === "cool"
                          ? "🆒"
                          : k === "wow"
                          ? "😮"
                          : k === "music"
                          ? "🎶"
                          : "❓"}
                      </span>
                      <span className="mr-react-count">{reactionCounts[k] || 0}</span>
                    </button>
                  ))}
                </div>
              </div>
            </section>

            {/* DISCUSSION (replaces the manual comments UI) */}
            {basePath && (
              <section className="mr-section">
                <h3>Discussion</h3>
                {/* Discussion handles comment posting, listing, moderation UI — same component used by MissionDetail */}
                <Discussion basePath={basePath} authorId={station.createdBy} />
              </section>
            )}

            {/* META */}
            <div className="mr-meta">
              {station.createdAtMs ? (
                <span>Added {new Date(station.createdAtMs).toLocaleDateString()}</span>
              ) : null}
              {station.slug ? (
                <span>
                  {" "}
                  • Slug: <code>{station.slug}</code>
                </span>
              ) : null}
              <span>
                {" "}
                • ID: <code>{station.id}</code>
              </span>
            </div>
          </>
        )}
      </div>
    </PageWrapper>
  );
}
